import { useCallback, useEffect } from 'react';
import useSWR, { useSWRConfig } from 'swr';

import config from '../../config';
import { channelWithoutProgramsPath } from '../../forces/channels/api';
import { getRequestPathWithAccessToken } from '../../forces/utils/request';
import { useAuthToken } from '../../hooks/useAuthToken';
import { captureExceptionInSentry } from '../../utils/errorTracker/tracking';

type UseTvGuideChannelsOptions = {
  includePrograms?: boolean;
};

export const useRevalidateTvGuideChannels = () => {
  const { cache, mutate } = useSWRConfig();
  return useCallback(() => {
    if (!(cache instanceof Map)) {
      return Promise.resolve();
    }
    const keys = Array.from(cache.keys()).filter((k: string) => k.includes('includePrograms=false'));
    const mutations = keys.map(key => mutate(key));
    return Promise.all(mutations);
  }, [cache, mutate]);
};

export const useTvGuideChannels = ({ includePrograms = false }: UseTvGuideChannelsOptions) => {
  const accessToken = useAuthToken();
  const cacheKey = channelWithoutProgramsPath(includePrograms);

  const { data, error, isLoading } = useSWR(
    cacheKey,
    () =>
      getRequestPathWithAccessToken<RiksTV.ClientAPI.ChannelResponse>(accessToken)(
        channelWithoutProgramsPath(includePrograms),
        { url: config.contentSearchApiUrl }
      ),
    {
      // refresh on focus change if more than 5 minutes on idle
      focusThrottleInterval: 300_000,
      // autorefresh every 30 min
      refreshInterval: 1_800_000,
    }
  );

  useEffect(() => {
    if (error) {
      captureExceptionInSentry(error);
    }
  }, [error]);

  return { channels: data, isLoading };
};
